import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getQualificationCandidate } from '@/services/api'
import { Button } from '@/components/ui/button'

const CATEGORY_STYLES: Record<string, string> = {
  green: 'bg-green-100 text-green-800 border-green-300',
  orange: 'bg-orange-100 text-orange-800 border-orange-300',
  yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  red: 'bg-red-100 text-red-800 border-red-300',
}

const CATEGORY_LABELS: Record<string, string> = {
  green: 'Vert',
  orange: 'Orange',
  yellow: 'Jaune',
  red: 'Rouge',
}

export default function QualificationCandidateDetail() {
  const { id } = useParams<{ id: string }>()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['qualification-candidate', id],
    queryFn: () => getQualificationCandidate(id!),
    enabled: !!id,
    retry: 1,
  })

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-bronze shadow-md">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <Link to="/ops-portal/qualification-candidates" className="text-white/70 hover:text-white text-sm">
              &larr; Retour à la liste
            </Link>
            <h1 className="text-xl font-bold text-white mt-1">
              {data?.institution_name || 'Candidat'}
            </h1>
          </div>
          {data?.category && (
            <span
              className={`inline-block px-3 py-1 rounded text-sm font-semibold border ${
                CATEGORY_STYLES[data.category] || 'bg-gray-100 text-gray-800 border-gray-300'
              }`}
            >
              {CATEGORY_LABELS[data.category] || data.category}
            </span>
          )}
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {isLoading && <div className="text-muted-foreground">Chargement...</div>}
        {isError && <div className="text-destructive">Erreur lors du chargement</div>}

        {data && (
          <div className="space-y-6">
            <Section title="Institution">
              <Field label="Nom" value={data.institution_name} />
              <Field label="Nom court" value={data.institution_short_name} />
              <Field label="Soumis le" value={new Date(data.created_at).toLocaleString('fr-FR')} />
              <Field label="ID candidat" value={data.id} mono />
              {data.gouvernance_interne && (
                <div className="md:col-span-2">
                  <dt className="text-sm font-medium text-muted-foreground">Gouvernance interne</dt>
                  <dd className="mt-1 text-foreground whitespace-pre-wrap">{data.gouvernance_interne}</dd>
                </div>
              )}
            </Section>

            <Section title="Échelle & activité">
              <Field label="Clients actifs" value={data.active_clients} />
              <Field label="Encours dépôts" value={data.deposit_outstanding} />
              <Field label="Nombre d'agences" value={String(data.agency_count)} />
              <Field
                label="Pays d'opération"
                value={
                  Array.isArray(data.multi_country) && data.multi_country.length
                    ? data.multi_country.join(', ')
                    : '-'
                }
              />
              <Field label="W2B / B2W offert" value={data.w2b_b2w_offered} />
            </Section>

            <Section title="Core Banking">
              <Field label="Logiciel CBS" value={data.cbs_software} />
              <Field label="CBS autre" value={data.cbs_software_other} />
              <Field label="Contact pris avec l'éditeur" value={data.cbs_contact_status} />
              <Field label="Capacité d'évolution" value={data.cbs_evolution_capacity} />
              <Field label="Historique" value={data.cbs_history} />
            </Section>

            <Section title="Conformité & BCEAO">
              <Field label="Statut d'inscription" value={data.bceao_enrollment_status} />
              <Field label="Étape" value={data.bceao_progress_stage} />
              <Field label="Sponsor identifié" value={data.sponsor_identified} />
              <Field label="Dispositif LBC/FT" value={data.lbc_ft_dispositif} />
              <Field label="Reporting régulateur" value={data.reporting_regulateur} />
            </Section>

            <Section title="Engagement">
              <Field label="Intérêt" value={data.qualification_interest} />
              <Field label="Conscience deadline 30/06" value={data.deadline_awareness} />
              <Field label="Disponibilité d'appel" value={data.call_availability} />
            </Section>

            <Section title="Contact">
              <Field label="Nom" value={data.contact_name} />
              <Field label="Rôle" value={data.contact_role} />
              <Field label="Téléphone" value={data.contact_phone} />
              <Field label="Email" value={data.contact_email} />
              <Field label="Newsletter" value={data.newsletter_optin ? 'Oui' : 'Non'} />
            </Section>

            {data.free_comment && (
              <Section title="Commentaire libre">
                <div className="md:col-span-2 whitespace-pre-wrap text-foreground">
                  {data.free_comment}
                </div>
              </Section>
            )}

            <div className="pt-4">
              <a href={`mailto:${data.contact_email}`}>
                <Button className="bg-gold hover:bg-gold/90">Répondre par email</Button>
              </a>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-border p-6">
      <h2 className="text-lg font-semibold text-bronze mb-4 pb-2 border-b border-border">{title}</h2>
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">{children}</dl>
    </div>
  )
}

function Field({ label, value, mono }: { label: string; value?: string | null; mono?: boolean }) {
  if (!value) return null
  return (
    <div>
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className={`mt-1 text-foreground ${mono ? 'font-mono text-sm break-all' : ''}`}>{value}</dd>
    </div>
  )
}
